import { ComponentStore } from '@ngrx/component-store';
import { inject, Injectable } from '@angular/core';
import { catchError, concatMap } from 'rxjs/operators';
import { EMPTY, forkJoin, Observable, of } from 'rxjs';
import equal from 'fast-deep-equal';
import { IGcComponentStore } from '@app/core/interfaces/gc-component-store.interface';
import { FLOATING_POPUP_DATA } from '@app/main/main-content/floating-popup-manager/providers/floating-popup-data.token';
import { Store } from '@ngrx/store';
import { selectMainProjectId } from '@app/main/main-screen/store/main-selection/main-shared-selection/main-shared-selection.selectors';
import { FloatingPopupService } from '@app/main/main-content/floating-popup-manager/services/floating-popup.service';
import {
  FpLoggerNoiseMinNoiseActionGetData,
  FpLoggerNoiseMinNoiseActionGetDataError,
  FpLoggerNoiseMinNoiseActionGetDataSuccesses,
  FpLoggerNoiseMinNoiseActions,
  FpLoggerNoiseMinNoiseActionToggleChartFullscreen,
  FpLoggerNoiseMinNoiseActionType,
  FpLoggerNoiseMinNoiseActionUpdatePeriod,
} from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/tabs/fp-logger-noise-min-noise/services/fp-logger-noise-min-noise/fp-logger-noise-min-noise.actions';
import {
  IFpLoggerNoiseMinNoiseState,
  initialFpLoggerNoiseMinNoiseState,
} from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/tabs/fp-logger-noise-min-noise/services/fp-logger-noise-min-noise/fp-logger-noise-min-noise.state';
import { LoggerDeploymentChartDataApiService } from '@app/api/services/logger-deployment-chart-data-api.service';
import { DateRangeComparisonService } from '@app/core/date-range/services/date-range-comparison.service';
import { MonthsDateRangeTypeConfig } from '@app/core/date-range/configs/months-date-range-type.config';
import { convertDateChartDataInArray } from '@app/shared/utils/date-chart-data';
import {
  ILowestDecibelDateChartDataRange,
  ILowestDecibelDateChartDataValue,
} from '@app/api/interfaces/chart-data/date-lowest-decibel';
import { DateChartDataService } from '@app/core/services/date-chart-data/date-chart-data.service';
import {
  ICombinedMinNoiseChartData,
  IEmptyLowestDecibelDateChartDataValue,
} from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/tabs/fp-logger-noise-min-noise/interfaces/combined-min-noise-chart-data';
import { ICombinedDateChartData } from '@app/api/interfaces/chart-data/base-date-chart-data';
import { ILdLogger } from '@app/main/leak-detection/interfaces/ld-logger';

@Injectable()
export class FpLoggerNoiseMinNoiseStore
  extends ComponentStore<IFpLoggerNoiseMinNoiseState>
  implements IGcComponentStore<FpLoggerNoiseMinNoiseActions>
{
  public readonly data = inject<ILdLogger>(FLOATING_POPUP_DATA);
  protected readonly dateRangeService = inject(DateRangeComparisonService);
  protected readonly dateChartDataService = inject(DateChartDataService);
  protected readonly store = inject(Store);
  protected readonly chartDataApiService = inject(LoggerDeploymentChartDataApiService);
  public readonly popupService: FloatingPopupService = inject(FloatingPopupService);

  constructor() {
    super(initialFpLoggerNoiseMinNoiseState);
  }

  public readonly date$ = this.select(s => s.date, { equal });
  public readonly popupMainData$ = this.select(s => s.popupMainData, { equal });
  public readonly data$ = this.select(s => s.data, { equal });
  public readonly period$ = this.select(s => s.period, { equal });
  public readonly error$ = this.select(s => s.error, { equal });

  private mainProjectId = this.store.selectSignal(selectMainProjectId);

  public readonly loading$ = this.select(s => s.loading, { equal });

  public readonly isChartFullscreenActive$ = this.select(s => s.isChartFullscreenActive, { equal });

  public dispatch = this.effect((action$: Observable<FpLoggerNoiseMinNoiseActions>) =>
    action$.pipe(
      concatMap(action => {
        switch (action.type) {
          case FpLoggerNoiseMinNoiseActionType.GetData: {
            this.reducerGetData();
            this.effectGetData(action);
            break;
          }
          case FpLoggerNoiseMinNoiseActionType.GetDataSuccesses: {
            this.reducerGetDataSuccesses(action);
            break;
          }
          case FpLoggerNoiseMinNoiseActionType.GetDataError: {
            this.reducerGetDataError(action);
            break;
          }
          case FpLoggerNoiseMinNoiseActionType.UpdatePeriod: {
            this.reducerUpdatePeriod(action);
            this.effectUpdatePeriod();
            break;
          }
          case FpLoggerNoiseMinNoiseActionType.ToggleChartFullscreen: {
            this.reducerToggleChartFullscreen();
            this.effectToggleChartFullscreen(action);
            break;
          }
        }
        return of(action);
      })
    )
  );

  private reducerGetData() {
    this.patchState({
      loading: true,
      error: null,
    });
  }

  private reducerGetDataSuccesses(action: FpLoggerNoiseMinNoiseActionGetDataSuccesses) {
    const state = this.get();

    this.patchState({
      date: action.date,
      cache: {
        ...state.cache,
        dateRange:
          action.cacheDateRange === null || !!action.cacheDateRange ? action.cacheDateRange : state.cache.dateRange,
        data: action.cacheData === null || !!action.cacheData ? action.cacheData : state.cache.data,
      },
      popupMainData: action.popupMainData,
      data: action.data,
      loading: false,
    });
  }

  private reducerGetDataError(action: FpLoggerNoiseMinNoiseActionGetDataError) {
    this.patchState({
      date: action.date,
      popupMainData: action.popupMainData,
      data: null,
      error: action.error,
      loading: false,
    });
  }

  private reducerUpdatePeriod(action: FpLoggerNoiseMinNoiseActionUpdatePeriod) {
    this.patchState({
      period: action.period,
    });
  }

  private reducerToggleChartFullscreen() {
    const state = this.get();
    this.patchState({
      isChartFullscreenActive: !state.isChartFullscreenActive,
    });
  }

  private effectGetData(action: FpLoggerNoiseMinNoiseActionGetData) {
    const { deployment } = action.popupMainData || {};
    const mainProjectId = this.mainProjectId();
    const state = this.get();

    if (!mainProjectId || !deployment) {
      this.dispatch({
        type: FpLoggerNoiseMinNoiseActionType.GetDataSuccesses,
        date: action.date,
        popupMainData: action.popupMainData,
        data: null,
      });

      return;
    }

    const monthsPeriod = MonthsDateRangeTypeConfig[state.period];

    const { isCacheInvalid, isCacheContainData, cacheDateRange, showDateRange, dateRangesForRequest } =
      this.dateChartDataService.manageCacheDate(state.cache.dateRange, action.date, monthsPeriod);

    if (isCacheContainData) {
      const data = this.dateChartDataService.filterDataAccordingToTheRange<ICombinedMinNoiseChartData>(
        showDateRange,
        state.cache.data || [],
        el => el.lowestNoiseDecibel === null
      );

      this.dispatch({
        type: FpLoggerNoiseMinNoiseActionType.GetDataSuccesses,
        date: action.date,
        popupMainData: action.popupMainData,
        data,
      });

      return;
    }

    const requests = dateRangesForRequest.map(dRange =>
      this.chartDataApiService.getDateLowestDecibel(mainProjectId, deployment.deploymentId, {
        ...dRange,
        includeRelatedDeployments: true,
      })
    );

    const oldCacheData = isCacheInvalid ? [] : state.cache.data || [];

    forkJoin(requests)
      .pipe(
        catchError(error => {
          this.dispatch({
            type: FpLoggerNoiseMinNoiseActionType.GetDataError,
            date: action.date,
            popupMainData: action.popupMainData,
            error,
          });
          return EMPTY;
        })
      )
      .subscribe(requestsData => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const arrayOfData = (requestsData as any[]).map(el =>
          convertDateChartDataInArray<ILowestDecibelDateChartDataValue, ILowestDecibelDateChartDataRange>(el.data)
        );

        const responseData: ICombinedMinNoiseChartData[] = arrayOfData.reduce(
          (acc, convertedData) => acc.concat(convertedData),
          []
        );

        const rawCacheData = [...oldCacheData, ...responseData];

        const cacheData = this.dateChartDataService.fillAllEmptyDateDataAccordingToTheRange(
          cacheDateRange,
          rawCacheData,
          date => {
            const emptyObj: ICombinedDateChartData<
              IEmptyLowestDecibelDateChartDataValue,
              ILowestDecibelDateChartDataRange
            > = {
              date,
              deploymentId: deployment.deploymentId,
              deployed: deployment.deployTime,
              collected: deployment.deployTime,
              serialNo: deployment.serialNo,
              lowestNoiseDecibel: null,
            };

            return emptyObj;
          }
        );

        const data = this.dateChartDataService.filterDataAccordingToTheRange<ICombinedMinNoiseChartData>(
          showDateRange,
          cacheData || [],
          el => el.lowestNoiseDecibel === null
        );

        this.dispatch({
          type: FpLoggerNoiseMinNoiseActionType.GetDataSuccesses,
          date: action.date,
          cacheData,
          cacheDateRange,
          popupMainData: action.popupMainData,
          data,
        });
      });
  }

  private effectUpdatePeriod() {
    const state = this.get();

    this.dispatch({
      type: FpLoggerNoiseMinNoiseActionType.GetData,
      date: state.date,
      popupMainData: state.popupMainData,
    });
  }

  private effectToggleChartFullscreen(action: FpLoggerNoiseMinNoiseActionToggleChartFullscreen) {
    if (!action.synchronizeWithPopupId) return;

    const state = this.get();

    if (state.isChartFullscreenActive) {
      this.popupService.enterFullscreen(action.synchronizeWithPopupId, false);
    } else {
      this.popupService.exitFullscreen(action.synchronizeWithPopupId, false);
    }
  }
}
